import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import apiService from '../../services/api';
import AuthPageLayout, { authTextFieldSx, authSubmitButtonSx, authTextLinkSx } from './AuthPageLayout';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [validating, setValidating] = useState(!!token);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      if (!token) {
        setTokenValid(false);
        setValidating(false);
        return;
      }

      try {
        const response = await apiService.validateResetToken(token);
        if (!cancelled) {
          setTokenValid(!!response?.data?.valid);
        }
      } catch {
        if (!cancelled) {
          setTokenValid(false);
        }
      } finally {
        if (!cancelled) {
          setValidating(false);
        }
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (
      newPassword.length < 8 ||
      !/\d/.test(newPassword) ||
      !/[^A-Za-z0-9]/.test(newPassword)
    ) {
      setError('Password must be at least 8 characters and include at least 1 number and 1 special character');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await apiService.resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  const passwordInputProps = (visible, onToggle, isMobile) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Lock style={{ color: 'var(--color-text-secondary)' }} />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={onToggle}
          edge="end"
          sx={{
            color: 'var(--color-text-secondary)',
            minWidth: isMobile ? '44px' : 'auto',
            minHeight: isMobile ? '44px' : 'auto'
          }}
        >
          {visible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
    style: {
      fontSize: isMobile ? '16px' : '14px',
      minHeight: isMobile ? '48px' : '52px'
    }
  });

  return (
    <AuthPageLayout
      compact
      title={success ? 'Password updated' : 'Reset password'}
      subtitle={success
        ? 'You can now sign in with your new password.'
        : 'Choose a new password for your account.'}
    >
      {(isMobile) => {
        if (validating) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: 'var(--color-accent)' }} />
            </Box>
          );
        }

        if (success) {
          return (
            <Box sx={{ width: '100%' }}>
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  background: 'rgba(76, 175, 80, 0.12)',
                  border: '1px solid rgba(76, 175, 80, 0.35)',
                  color: 'var(--color-text)',
                  '& .MuiAlert-icon': { color: 'var(--color-success, #4caf50)' }
                }}
              >
                Your password has been reset successfully.
              </Alert>
              <Button
                component={Link}
                to="/login"
                fullWidth
                variant="contained"
                sx={authSubmitButtonSx(isMobile)}
              >
                Sign in
              </Button>
            </Box>
          );
        }

        if (!tokenValid) {
          return (
            <Box sx={{ width: '100%' }}>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  color: 'var(--color-text)'
                }}
              >
                This reset link is invalid or has expired. Please request a new one.
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button component={Link} to="/forgot-password" variant="text" sx={authTextLinkSx(isMobile)}>
                  Request a new link
                </Button>
                <Button component={Link} to="/login" variant="text" sx={authTextLinkSx(isMobile)}>
                  Back to sign in
                </Button>
              </Box>
            </Box>
          );
        }

        return (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  color: 'var(--color-text)'
                }}
              >
                {error}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={passwordInputProps(showNewPassword, () => setShowNewPassword(!showNewPassword), isMobile)}
              sx={{ ...authTextFieldSx(isMobile), mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={passwordInputProps(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), isMobile)}
              sx={{ ...authTextFieldSx(isMobile), mb: 1 }}
            />
            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
              Must be at least 8 characters and include a number and a special character
            </Typography>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !newPassword || !confirmPassword}
              sx={authSubmitButtonSx(isMobile)}
            >
              {loading ? (
                <CircularProgress size={isMobile ? 24 : 20} sx={{ color: 'var(--color-text)' }} />
              ) : (
                'Reset password'
              )}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button component={Link} to="/login" variant="text" sx={authTextLinkSx(isMobile)}>
                Back to sign in
              </Button>
            </Box>
          </Box>
        );
      }}
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
